import { isPresent, isNone } from '@ember/utils';
import { computed } from '@ember/object';
import { alias, reads } from '@ember/object/computed';
import { attr, belongsTo, hasMany } from '@ember-data/model';
import { inject as service } from '@ember/service';
import { getOwner } from '@ember/application';
import ItemsV3Base from '@square/dashboard-items-data/models/items-v3-base';
import { hasEmptyIntersection } from '@square/dashboard-utilities/utils/intersection';
import NumericEnum from '@square/dashboard-utilities/utils/numeric-enum';
import captureItemFeeMembershipConstraint from '@square/dashboard-utilities/utils/sentry/capture-item-fee-membership-constraint';
const AdjustmentType = NumericEnum.create(['TAX', 'SURCHARGE']);
const InclusionType = NumericEnum.create(['ADDITIVE', 'INCLUSIVE']);
const CalculationPhase = NumericEnum.create(['DISCOUNT_PERCENTAGE_PHASE', 'DISCOUNT_AMOUNT_PHASE', 'FEE_SUBTOTAL_PHASE', 'FEE_TOTAL_PHASE', 'TIP_PHASE', 'SWEDISH_ROUNDING_PHASE', 'SURCHARGE_PHASE', 'SURCHARGE_TOTAL_PHASE']);
const ExemptionType = NumericEnum.create(['AMOUNT', 'DINING', 'NO_CONDITION']);
export const MAX_FEE_DECIMAL_PRECISION = 5;
export default ItemsV3Base.extend({
  intl: service(),
  name: attr('string'),
  percentage: attr('tax-percentage'),
  calculationPhase: attr('enum', {
    enumClass: CalculationPhase
  }),
  inclusionType: attr('enum', {
    enumClass: InclusionType
  }),
  feeTypeId: attr('string'),
  feeTypeName: attr('string'),
  adjustmentType: attr('enum', {
    enumClass: AdjustmentType
  }),
  appliesToCustomAmounts: attr('boolean', {
    defaultValue: true
  }),
  enabled: attr('boolean', {
    allowNull: true
  }),
  isSecondaryTax: attr('boolean', {
    defaultValue: false
  }),
  // The productSet and adjustmentTax objects automatically save whenever this Fee object is saved.
  taxRules: hasMany('tax-rule', {
    autosaved: ['productSet', 'adjustmentTax']
  }),
  taxRule: alias('taxRules.firstObject'),
  itemFeeMemberships: hasMany('item-fee-membership', {
    async: false
  }),
  surchargeFeeMemberships: hasMany('surcharge-fee-membership', {
    async: false
  }),
  productSet: belongsTo('product-set', {
    async: false
  }),
  hasSelectedAllItems: attr('boolean', {
    defaultValue: false
  }),
  itemFeeMembershipForItem(item) {
    return isPresent(item.id) ? this.itemFeeMemberships.findBy('item.id', item.id) : this.itemFeeMemberships.findBy('item', item);
  },
  productSetMembershipForItem(item) {
    if (!this.productSet || !this.enabled) {
      return false;
    }
    if (this.productSet.matchAllProducts) {
      return true;
    }
    return this.productSet.items.includes(item) || this.productSet.categories.includes(item.menuCategory);
  },
  addFeeMembership(item, forceNew = false) {
    if (isPresent(this.productSet) && !this.productSet.isDeleted) {
      captureItemFeeMembershipConstraint(item.id, this.id);
      return null;
    }
    let membership = null;
    if (!forceNew) {
      membership = this.itemFeeMembershipForItem(item);
    }
    if (isNone(membership)) {
      this.watchRelationship('itemFeeMemberships', () => {
        membership = this.store.createRecord('item-fee-membership', {
          item,
          fee: this
        });
      });
      membership.setMerchantSubunits(this.commonSubunits(item));
    } else {
      membership.rollbackAttributes();
    }
    return membership;
  },
  removeFeeMembership(item, membership) {
    if (isNone(membership)) {
      membership = this.itemFeeMembershipForItem(item);
    }
    if (!isNone(membership)) {
      this.watchRelationship('itemFeeMemberships', () => {
        membership.deleteRecord();
      });
    }
  },
  revert() {
    this.rollbackAttributes();
    this.surchargeFeeMemberships.forEach(surchargeFeeMembership => {
      if (surchargeFeeMembership) {
        surchargeFeeMembership.rollbackAttributes();
      }
    });
    this.itemFeeMemberships.forEach(itemFeeMembership => {
      if (itemFeeMembership) {
        itemFeeMembership.rollbackAttributes();
      }
    });
  },
  async save() {
    this._clearRelationshipTracking();
    // eslint-disable-next-line prefer-rest-params
    await this._super(...arguments);
  },
  updateGloballyEnabledForRelationships(globallyEnabled) {
    if (isPresent(this.productSet)) {
      this.productSet.set('globallyEnabled', globallyEnabled);
    }
  },
  addAffectedRelatedObjects(subunitToken) {
    const subunitToAdd = this.subunits.findBy('token', subunitToken);
    if (isPresent(subunitToAdd)) {
      if (isPresent(this.productSet)) {
        this.productSet.addMerchantSubunit(subunitToAdd);
      }
    }
  },
  removeAffectedRelatedObjects(subunitToken) {
    if (isPresent(this.productSet)) {
      this.productSet.removeMerchantSubunit(subunitToken);
    }

    // The location of tax rules must be a subset of the tax.
    // Whenever we remove a subunit from a tax, we can safely cascade this update down to the tax rule.
    if (isPresent(this.taxRules)) {
      this.taxRules.forEach(taxRule => {
        taxRule.removeMerchantSubunit(subunitToken);
        taxRule.removeAffectedRelatedObjects(subunitToken);
      });
    }
  },
  // TODO (nrobinson): This is a hack. I have an issue open in the repo for this library to come up with a better fix
  // https://github.com/ef4/ember-data-relationship-tracker/issues/22
  _clearRelationshipTracking() {
    this._relationshipTracker = Object.create(null);
    this.notifyPropertyChange('relationshipTrackerVersion');
  },
  isNotApplicableToItem(item) {
    const membership = item.itemFeeMembershipForFee(this);
    if (!isNone(membership) && !membership.isDeleted) {
      return false;
    }
    return hasEmptyIntersection(this.subunits.mapBy('token'), item.subunits.mapBy('token'));
  },
  isApplicableToItem(item) {
    return !this.isNotApplicableToItem(item);
  },
  status: computed('enabled', 'intl', function () {
    return this.enabled ? this.intl.t('common.enabled') : this.intl.t('common.disabled');
  }),
  isDirty: computed('hasDirtyFields', 'hasSelectedAllItems', 'itemFeeMemberships.@each.isDirty', 'productSet.{isDeleted,isDirty}', 'surchargeFeeMemberships.@each.isDirty', 'taxRule.isDirty', function (...args) {
    if (this.productSet?.isDirty || this.productSet?.isDeleted || this.taxRule?.isDirty || (this.itemFeeMemberships || []).any(o => o && o.isDirty) || (this.surchargeFeeMemberships || []).any(sfm => sfm && sfm.isDirty) || this.hasSelectedAllItems) {
      return true;
    }
    return this._super(...args);
  }),
  needsMigration: computed('itemFeeMemberships.length', 'productSet', function () {
    return !this.productSet || this.itemFeeMemberships?.length > 0;
  }),
  relationshipTrackerVersion: reads('version'),
  // This clears the fields set on adjustment taxes by the Tax Rule Config modal.
  clearAdjustmentTaxFields() {
    this.set('percentage', null);
  },
  copyAsAdjustmentTax() {
    const adjustmentTax = this.store.modelFor('fee').createDefault(this.store);
    adjustmentTax.setProperties({
      isSecondaryTax: true,
      name: this.name,
      percentage: null
    });
    return adjustmentTax;
  },
  // Copy relevant fields from this fee to the adjustment tax
  copyToAdjustmentTax(adjustmentTax) {
    adjustmentTax.setProperties({
      name: this.name
    });
  }
}).reopenClass({
  AdjustmentType,
  InclusionType,
  CalculationPhase,
  ExemptionType,
  MAXIMUM_FRACTION_DIGITS: 5,
  catalogType() {
    return 'FEE';
  },
  sortKey: 'name',
  createDefaultOptions(store) {
    const application = getOwner(store);
    const userInfo = application.lookup('service:user-info');
    const intl = application.lookup('service:intl');
    const defaultFeeInfo = userInfo.countryFeatures.feeTypes[0];
    const options = {
      feeTypeId: defaultFeeInfo.value,
      // eslint-disable-next-line square/no-translation-key-interpolation
      feeTypeName: intl.t(`feeTypes.${defaultFeeInfo.labelKey}`),
      inclusionType: InclusionType[userInfo.countryFeatures.feeInclusionType],
      calculationPhase: CalculationPhase.FEE_SUBTOTAL_PHASE,
      adjustmentType: AdjustmentType.TAX,
      appliesToCustomAmounts: true,
      enabled: true,
      percentage: 0,
      isSecondaryTax: false
    };
    return options;
  },
  _modelNameForMigration: 'fee'
});